import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createAudioTranscription } from '../../lib/chat-api';
import {
  mergeVoiceTranscript,
  normalizeVoiceTranscriptionLanguageCode,
} from './chat-composer.logic';

const AUDIO_PROCESSOR_BUFFER_SIZE = 4096;
const TARGET_COMPRESSED_SAMPLE_RATE = 16000;
const COMPRESSED_RECORDING_AUDIO_BITS_PER_SECOND = 24000;

type RecordingFormat = {
  mimeType: string;
  fileExtension: string;
};

const PREFERRED_COMPRESSED_RECORDING_FORMATS: RecordingFormat[] = [
  {
    mimeType: 'audio/webm;codecs=opus',
    fileExtension: 'webm',
  },
  {
    mimeType: 'audio/ogg;codecs=opus',
    fileExtension: 'ogg',
  },
  {
    mimeType: 'audio/mp4;codecs=mp4a.40.2',
    fileExtension: 'm4a',
  },
  {
    mimeType: 'audio/mp4',
    fileExtension: 'm4a',
  },
];

type RecordingSession = {
  stream: MediaStream;
  audioContext: AudioContext;
  source: MediaStreamAudioSourceNode;
  processor: ScriptProcessorNode;
  muteGain: GainNode;
  captureDestination?: MediaStreamAudioDestinationNode;
  mediaRecorder?: MediaRecorder;
  recordingFileExtension?: string;
  mediaRecorderStopPromise?: Promise<Blob>;
  chunks: Float32Array[];
  sampleRate: number;
  recordedSamples: number;
};

type StoppedVoiceRecording = {
  file: Blob;
  fileName: string;
  durationSeconds: number;
  autoSend: boolean;
};

export type ChatComposerVoiceInputState =
  | {
      phase: 'idle';
      duration: 0;
    }
  | {
      phase: 'recording' | 'transcribing';
      duration: number;
    };

type UseChatComposerVoiceInputOptions = {
  draft: string;
  disabled: boolean;
  canCancel: boolean;
  isSubmitting: boolean;
  attachments: Array<{ status: string }>;
  onDraftChange: (value: string) => void;
  onSubmit: (options?: { textOverride?: string }) => Promise<void> | void;
};

function normalizeLiveLevel(level: number) {
  return Math.min(Math.max((level - 0.02) / 0.98, 0), 1);
}

function resolveCompressedRecordingFormat(): RecordingFormat | null {
  if (typeof MediaRecorder === 'undefined') {
    return null;
  }

  for (const format of PREFERRED_COMPRESSED_RECORDING_FORMATS) {
    if (MediaRecorder.isTypeSupported(format.mimeType)) {
      return format;
    }
  }

  return null;
}

function downsamplePCM(
  pcmData: Float32Array,
  sourceSampleRate: number,
  targetSampleRate: number
) {
  if (
    !Number.isFinite(sourceSampleRate) ||
    sourceSampleRate <= 0 ||
    sourceSampleRate <= targetSampleRate
  ) {
    return {
      pcmData,
      sampleRate: sourceSampleRate,
    };
  }

  const sampleRateRatio = sourceSampleRate / targetSampleRate;
  const nextLength = Math.max(1, Math.round(pcmData.length / sampleRateRatio));
  const result = new Float32Array(nextLength);

  let sourceOffset = 0;
  for (let index = 0; index < nextLength; index += 1) {
    const nextSourceOffset = Math.min(
      pcmData.length,
      Math.round((index + 1) * sampleRateRatio)
    );

    let sum = 0;
    let count = 0;
    for (
      let sampleIndex = sourceOffset;
      sampleIndex < nextSourceOffset;
      sampleIndex += 1
    ) {
      sum += pcmData[sampleIndex] ?? 0;
      count += 1;
    }

    result[index] = count > 0 ? sum / count : (pcmData[sourceOffset] ?? 0);
    sourceOffset = nextSourceOffset;
  }

  return {
    pcmData: result,
    sampleRate: targetSampleRate,
  };
}

function mergePCMChunks(chunks: Float32Array[]) {
  const totalLength = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const merged = new Float32Array(totalLength);

  let offset = 0;
  for (const chunk of chunks) {
    merged.set(chunk, offset);
    offset += chunk.length;
  }

  return merged;
}

function writeWAVString(view: DataView, offset: number, value: string) {
  for (let index = 0; index < value.length; index += 1) {
    view.setUint8(offset + index, value.charCodeAt(index));
  }
}

function encodeWAV(pcmData: Float32Array, sampleRate: number) {
  const bytesPerSample = 2;
  const dataSize = pcmData.length * bytesPerSample;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  writeWAVString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeWAVString(view, 8, 'WAVE');
  writeWAVString(view, 12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);
  view.setUint16(22, 1, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * bytesPerSample, true);
  view.setUint16(32, bytesPerSample, true);
  view.setUint16(34, 16, true);
  writeWAVString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let index = 0; index < pcmData.length; index += 1) {
    const sample = Math.max(-1, Math.min(1, pcmData[index] ?? 0));
    const intSample = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
    view.setInt16(offset, intSample, true);
    offset += bytesPerSample;
  }

  return buffer;
}

async function closeRecordingSession(session: RecordingSession | null) {
  if (!session) {
    return;
  }

  if (session.mediaRecorder) {
    if (session.mediaRecorder.state !== 'inactive') {
      session.mediaRecorder.stop();
    }
    await session.mediaRecorderStopPromise?.catch(() => null);
  }

  session.processor.onaudioprocess = null;
  session.source.disconnect();
  session.processor.disconnect();
  session.captureDestination?.disconnect();
  session.muteGain.disconnect();
  session.stream.getTracks().forEach(track => track.stop());
  await session.audioContext.close();
}

function createVoiceInputError(message: string) {
  return new Error(message);
}

function normalizeVoiceRecordingError(error: unknown) {
  if (error instanceof DOMException) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') {
      return createVoiceInputError(
        'Microphone access is required to record voice input.'
      );
    }
    if (error.name === 'NotFoundError') {
      return createVoiceInputError('No microphone was found.');
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error;
  }

  return createVoiceInputError('Failed to start voice recording.');
}

async function startRecordingSession(
  onSample: (level: number, duration: number) => void
) {
  if (!navigator.mediaDevices?.getUserMedia) {
    throw createVoiceInputError(
      'Microphone recording is not supported in this environment.'
    );
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    audio: {
      channelCount: 1,
      sampleRate: TARGET_COMPRESSED_SAMPLE_RATE,
    },
  });

  let audioContext: AudioContext;
  try {
    audioContext = new AudioContext({
      sampleRate: TARGET_COMPRESSED_SAMPLE_RATE,
    });
  } catch {
    audioContext = new AudioContext();
  }
  const source = audioContext.createMediaStreamSource(stream);
  const processor = audioContext.createScriptProcessor(
    AUDIO_PROCESSOR_BUFFER_SIZE,
    1,
    1
  );
  const muteGain = audioContext.createGain();
  muteGain.gain.value = 0;
  const compressedFormat = resolveCompressedRecordingFormat();
  const captureDestination = compressedFormat
    ? audioContext.createMediaStreamDestination()
    : undefined;

  let mediaRecorder: MediaRecorder | undefined;
  let mediaRecorderStopPromise: Promise<Blob> | undefined;
  let recordedBlobs: Blob[] | undefined;

  if (captureDestination && compressedFormat) {
    recordedBlobs = [];
    const recorder = new MediaRecorder(captureDestination.stream, {
      mimeType: compressedFormat.mimeType,
      audioBitsPerSecond: COMPRESSED_RECORDING_AUDIO_BITS_PER_SECOND,
    });
    mediaRecorder = recorder;
    mediaRecorderStopPromise = new Promise<Blob>((resolve, reject) => {
      recorder.addEventListener('dataavailable', event => {
        if (event.data.size > 0) {
          recordedBlobs?.push(event.data);
        }
      });
      recorder.addEventListener('stop', () => {
        resolve(
          new Blob(recordedBlobs, {
            type: recorder.mimeType || compressedFormat.mimeType,
          })
        );
      });
      recorder.addEventListener('error', () => {
        reject(createVoiceInputError('Failed to encode compressed audio.'));
      });
    });
  }

  const session: RecordingSession = {
    stream,
    audioContext,
    source,
    processor,
    muteGain,
    captureDestination,
    mediaRecorder,
    mediaRecorderStopPromise,
    recordingFileExtension: compressedFormat?.fileExtension,
    chunks: [],
    sampleRate: audioContext.sampleRate,
    recordedSamples: 0,
  };

  processor.onaudioprocess = event => {
    const input = event.inputBuffer.getChannelData(0);
    session.chunks.push(new Float32Array(input));

    let peak = 0;
    for (let index = 0; index < input.length; index += 1) {
      peak = Math.max(peak, Math.abs(input[index] ?? 0));
    }

    session.recordedSamples += input.length;
    onSample(
      normalizeLiveLevel(peak),
      session.recordedSamples / session.sampleRate
    );
  };

  source.connect(processor);
  source.connect(captureDestination ?? muteGain);
  processor.connect(muteGain);
  muteGain.connect(audioContext.destination);
  mediaRecorder?.start();
  onSample(0, 0);

  return session;
}

async function stopRecordingSession(session: RecordingSession) {
  const durationSeconds = session.recordedSamples / session.sampleRate;

  if (durationSeconds <= 0) {
    throw createVoiceInputError('No speech was detected in the recording.');
  }

  let compressedFile: Blob | null = null;
  if (session.mediaRecorder && session.mediaRecorder.state !== 'inactive') {
    session.mediaRecorder.stop();
    compressedFile = (await session.mediaRecorderStopPromise) ?? null;
  }

  session.processor.onaudioprocess = null;
  session.source.disconnect();
  session.processor.disconnect();
  session.captureDestination?.disconnect();
  session.muteGain.disconnect();
  session.stream.getTracks().forEach(track => track.stop());
  await session.audioContext.close();

  if (compressedFile && compressedFile.size > 0) {
    return {
      file: compressedFile,
      fileName: `voice-input.${session.recordingFileExtension ?? 'webm'}`,
      durationSeconds,
    };
  }

  const mergedPCM = mergePCMChunks(session.chunks);
  if (mergedPCM.length === 0) {
    throw createVoiceInputError('No speech was detected in the recording.');
  }

  const downsampled = downsamplePCM(
    mergedPCM,
    session.sampleRate,
    TARGET_COMPRESSED_SAMPLE_RATE
  );
  const wavBuffer = encodeWAV(downsampled.pcmData, downsampled.sampleRate);

  return {
    file: new Blob([wavBuffer], { type: 'audio/wav' }),
    fileName: 'voice-input.wav',
    durationSeconds,
  };
}

export function useChatComposerVoiceInput({
  draft,
  disabled,
  canCancel,
  isSubmitting,
  attachments,
  onDraftChange,
  onSubmit,
}: UseChatComposerVoiceInputOptions) {
  const [phase, setPhase] = useState<'idle' | 'recording' | 'transcribing'>(
    'idle'
  );
  const [duration, setDuration] = useState(0);
  const [voiceErrorMessage, setVoiceErrorMessage] = useState<string | null>(
    null
  );
  const [isStartingRecording, setIsStartingRecording] = useState(false);

  const sessionRef = useRef<RecordingSession | null>(null);
  const pendingRecordingRef = useRef<StoppedVoiceRecording | null>(null);
  const draftRef = useRef(draft);
  const liveLevelRef = useRef(0);
  const actionIdRef = useRef(0);
  const transcriptionAbortRef = useRef<AbortController | null>(null);
  const [hasRetryableRecording, setHasRetryableRecording] = useState(false);

  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);

  useEffect(() => {
    return () => {
      actionIdRef.current += 1;
      transcriptionAbortRef.current?.abort();
      transcriptionAbortRef.current = null;
      const session = sessionRef.current;
      sessionRef.current = null;
      pendingRecordingRef.current = null;
      void closeRecordingSession(session);
    };
  }, []);

  const resetVoiceInputPresentation = useCallback(() => {
    setPhase('idle');
    setDuration(0);
    liveLevelRef.current = 0;
  }, []);

  const cancelCurrentVoiceAction = useCallback(() => {
    actionIdRef.current += 1;
    transcriptionAbortRef.current?.abort();
    transcriptionAbortRef.current = null;
  }, []);

  const clearPendingRecording = useCallback(() => {
    pendingRecordingRef.current = null;
    setHasRetryableRecording(false);
  }, []);

  const retainPendingRecording = useCallback(
    (recording: StoppedVoiceRecording) => {
      pendingRecordingRef.current = recording;
      setHasRetryableRecording(true);
    },
    []
  );

  const handleVoiceFailure = useCallback(
    (error: unknown) => {
      const session = sessionRef.current;
      sessionRef.current = null;
      void closeRecordingSession(session);
      setIsStartingRecording(false);
      resetVoiceInputPresentation();
      setVoiceErrorMessage(
        error instanceof Error && error.message.trim()
          ? error.message
          : 'Voice transcription failed.'
      );
    },
    [resetVoiceInputPresentation]
  );

  const canStartVoiceRecording =
    phase === 'idle' &&
    !isStartingRecording &&
    !disabled &&
    !canCancel &&
    !isSubmitting;

  const canAutoSendVoiceRecording =
    !disabled &&
    !canCancel &&
    !isSubmitting &&
    !attachments.some(attachment => attachment.status === 'uploading');

  const canRetryVoiceTranscription =
    hasRetryableRecording &&
    phase === 'idle' &&
    !isStartingRecording &&
    !disabled &&
    !canCancel &&
    !isSubmitting;

  const voiceState = useMemo<ChatComposerVoiceInputState>(() => {
    if (phase === 'idle') {
      return {
        phase: 'idle',
        duration: 0,
      };
    }

    return {
      phase,
      duration,
    };
  }, [duration, phase]);

  const requestCancelVoiceInput = useCallback(() => {
    cancelCurrentVoiceAction();
    setVoiceErrorMessage(null);
    setIsStartingRecording(false);
    const session = sessionRef.current;
    sessionRef.current = null;
    clearPendingRecording();
    void closeRecordingSession(session);
    resetVoiceInputPresentation();
  }, [
    cancelCurrentVoiceAction,
    clearPendingRecording,
    resetVoiceInputPresentation,
  ]);

  const requestStartVoiceRecording = useCallback(() => {
    if (!canStartVoiceRecording) {
      return;
    }

    cancelCurrentVoiceAction();
    clearPendingRecording();
    setVoiceErrorMessage(null);
    resetVoiceInputPresentation();
    setIsStartingRecording(true);

    const actionId = actionIdRef.current;
    void startRecordingSession((level, nextDuration) => {
      if (actionId !== actionIdRef.current) {
        return;
      }

      setDuration(nextDuration);
      liveLevelRef.current =
        liveLevelRef.current > 0
          ? liveLevelRef.current * 0.72 + level * 0.28
          : level;
    })
      .then(session => {
        if (actionId !== actionIdRef.current) {
          void closeRecordingSession(session);
          return;
        }

        sessionRef.current = session;
        setPhase('recording');
        setIsStartingRecording(false);
      })
      .catch(error => {
        if (actionId !== actionIdRef.current) {
          return;
        }

        handleVoiceFailure(normalizeVoiceRecordingError(error));
      });
  }, [
    canStartVoiceRecording,
    cancelCurrentVoiceAction,
    clearPendingRecording,
    handleVoiceFailure,
    resetVoiceInputPresentation,
  ]);

  const transcribeStoppedRecording = useCallback(
    (recording: StoppedVoiceRecording, actionId: number) => {
      setDuration(recording.durationSeconds);
      setPhase('transcribing');

      const controller = new AbortController();
      transcriptionAbortRef.current = controller;

      void createAudioTranscription({
        file: recording.file,
        fileName: recording.fileName,
        durationSeconds: recording.durationSeconds,
        languageCode: normalizeVoiceTranscriptionLanguageCode(
          navigator.language
        ),
        signal: controller.signal,
      })
        .then(async response => {
          if (actionId !== actionIdRef.current) {
            return;
          }

          const transcript = response.text.trim();
          if (!transcript) {
            throw createVoiceInputError(
              'No speech was detected in the recording.'
            );
          }

          const mergedText = mergeVoiceTranscript(transcript, draftRef.current);

          transcriptionAbortRef.current = null;
          clearPendingRecording();
          resetVoiceInputPresentation();
          onDraftChange(mergedText);

          if (!recording.autoSend) {
            return;
          }

          try {
            await Promise.resolve(
              onSubmit({
                textOverride: mergedText,
              })
            );
          } catch {
            // Sending failures already surface through the chat page state.
          }
        })
        .catch(error => {
          if (error instanceof DOMException && error.name === 'AbortError') {
            if (actionId === actionIdRef.current) {
              transcriptionAbortRef.current = null;
              resetVoiceInputPresentation();
            }
            return;
          }

          if (actionId !== actionIdRef.current) {
            return;
          }

          transcriptionAbortRef.current = null;
          retainPendingRecording(recording);
          handleVoiceFailure(error);
        });
    },
    [
      clearPendingRecording,
      handleVoiceFailure,
      onDraftChange,
      onSubmit,
      resetVoiceInputPresentation,
      retainPendingRecording,
    ]
  );

  const finalizeVoiceRecording = useCallback(
    (autoSend: boolean) => {
      if (phase !== 'recording') {
        return;
      }

      const session = sessionRef.current;
      sessionRef.current = null;

      if (!session) {
        handleVoiceFailure(
          createVoiceInputError('No active voice recording was found.')
        );
        return;
      }

      cancelCurrentVoiceAction();
      const actionId = actionIdRef.current;

      void stopRecordingSession(session)
        .then(recording => {
          if (actionId !== actionIdRef.current) {
            return;
          }

          transcribeStoppedRecording(
            {
              ...recording,
              autoSend,
            },
            actionId
          );
        })
        .catch(error => {
          if (actionId !== actionIdRef.current) {
            return;
          }

          handleVoiceFailure(error);
        });
    },
    [
      cancelCurrentVoiceAction,
      handleVoiceFailure,
      phase,
      transcribeStoppedRecording,
    ]
  );

  const requestStopVoiceRecording = useCallback(() => {
    finalizeVoiceRecording(false);
  }, [finalizeVoiceRecording]);

  const requestSendVoiceRecording = useCallback(() => {
    finalizeVoiceRecording(true);
  }, [finalizeVoiceRecording]);

  const clearVoiceErrorMessage = useCallback(() => {
    setVoiceErrorMessage(null);
    clearPendingRecording();
  }, [clearPendingRecording]);

  const requestRetryVoiceTranscription = useCallback(() => {
    if (!canRetryVoiceTranscription) {
      return;
    }

    const recording = pendingRecordingRef.current;
    if (!recording) {
      clearPendingRecording();
      return;
    }

    cancelCurrentVoiceAction();
    setVoiceErrorMessage(null);
    const actionId = actionIdRef.current;
    transcribeStoppedRecording(recording, actionId);
  }, [
    canRetryVoiceTranscription,
    cancelCurrentVoiceAction,
    clearPendingRecording,
    transcribeStoppedRecording,
  ]);

  return {
    voiceState,
    liveLevelRef,
    voiceErrorMessage,
    clearVoiceErrorMessage,
    canStartVoiceRecording,
    canAutoSendVoiceRecording,
    canRetryVoiceTranscription,
    requestStartVoiceRecording,
    requestStopVoiceRecording,
    requestSendVoiceRecording,
    requestCancelVoiceInput,
    requestRetryVoiceTranscription,
  };
}
